import { Match, Switch, createSignal, onCleanup, onMount } from "solid-js";
import { LoginPage } from "~/features/auth/LoginPage";
import { InvitationAccept } from "~/features/school/InvitationAccept";
import type { DashboardEntry } from "~/features/school/team";
import { PrivacyNotice } from "~/features/school/PrivacyNotice";
import { SchoolRegistration } from "~/features/school/SchoolRegistration";
import type { AuthModule } from "~/features/auth/auth";
import { StudentDashboard } from "~/features/student/StudentDashboard";
import { TeacherDashboard } from "~/features/teacher/TeacherDashboard";
import type { HttpPort } from "~/lib/http";

export function App(props: { auth: AuthModule; http: HttpPort }) {
  const cachedSession = props.auth.current();
  const [session, setSession] = createSignal<ReturnType<AuthModule["current"]>>();
  const [entry, setEntry] = createSignal<DashboardEntry>();
  const [restoring, setRestoring] = createSignal(Boolean(cachedSession));
  const [location, setLocation] = createSignal({ path: window.location.pathname, search: window.location.search });
  const syncLocation = () => setLocation({ path: window.location.pathname, search: window.location.search });
  const goTo = (path: string) => { window.history.pushState({}, "", path); syncLocation(); };
  window.addEventListener("popstate", syncLocation);
  onCleanup(() => window.removeEventListener("popstate", syncLocation));
  const onInvitationPage = () => location().path === "/einladung";
  const onSchoolPage = () => location().path === "/schule" || location().path.startsWith("/schule/");

  onMount(() => {
    if (!cachedSession) return;
    void props.auth.restore()
      .then(setSession)
      .catch(() => setSession(undefined))
      .finally(() => setRestoring(false));
  });

  return (
    <Switch>
      <Match when={restoring()}>
        <div class="auth-container"><div class="loading-spinner" aria-label="Sitzung wird geladen" /></div>
      </Match>
      <Match when={location().path === "/datenschutz"}>
        <PrivacyNotice onBack={() => { if (window.history.length > 1) window.history.back(); else goTo("/"); }} />
      </Match>
      <Match when={onSchoolPage()}>
        <SchoolRegistration
          http={props.http}
          auth={props.auth}
          path={location().path}
          search={location().search}
          onAuthenticated={() => { setEntry("welcome-principal"); setSession(props.auth.current()); goTo("/"); }}
          onLogin={() => goTo("/")}
        />
      </Match>
      <Match when={onInvitationPage()}>
        <InvitationAccept
          http={props.http}
          auth={props.auth}
          search={location().search}
          onAuthenticated={() => { setEntry("welcome-teacher"); setSession(props.auth.current()); goTo("/"); }}
          onLogin={() => goTo("/")}
        />
      </Match>
      <Match when={session()?.user.role === "teacher"}>
        <TeacherDashboard session={session()!} auth={props.auth} http={props.http} entry={entry()} onLogout={() => { setEntry(undefined); setSession(undefined); }} />
      </Match>
      <Match when={session()?.user.role === "student"}>
        <StudentDashboard session={session()!} auth={props.auth} http={props.http} onLogout={() => setSession(undefined)} />
      </Match>
      <Match when={true}>
        <LoginPage auth={props.auth} onAuthenticated={(result, audience) => { setEntry(result.user.schoolRole === "owner" || audience === "principal" ? "principal" : undefined); setSession(props.auth.current()); }} />
      </Match>
    </Switch>
  );
}
